import { ArrowDropDownIcon } from "@mui/x-date-pickers-pro";
import { useQueries } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  FilterIcon,
  GKButton,
  Grid2,
  NoData,
  Tab,
  Tabs,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { GET_HOLDING } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { QueryKeys } from "utils/queryKey";
import { PortfolioDropdownInterface } from "utils/types";
import useDocumentTitle from "utils/useDocumentTitle";
import FilterModal from "./FilterModal";
import HoldingsInsightsNew from "./HoldingsInsightsNew";
import InsigntsPageContent from "./InsigntsPageContent";
import Performance from "./Performance";
import PortfolioBreakdown from "./PortfolioBreakdown";

const HoldingsInsights = () => {
  useDocumentTitle("Holdings Insights");

  const dispatch = useAppDispatch();

  const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([""]);

  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const { leadDetailsTabs } = useAppSelector((state) => state?.app?.tab);

  const [selectedItems, setSelectedItems] = useState<
    PortfolioDropdownInterface[]
  >([]);

  const [resetOverlapKey, setResetOverlapKey] = useState(0);

  const navigate = useNavigate();

  const [openModal, setIsOpenModal] = useState(false);

  const requestBody = useGenerateQueryParams("insights", {
    portfolios: selectedItems?.length ? JSON.stringify(selectedItems) : null,
  });

  const { data: holdingData, isFetching: holdingLoading } = useFetchQuery({
    key: ["INSIGHTS", requestBody, selectedItems],
    retry: 0,
    route: GET_HOLDING,
    requestBody,
  });

  const [
    { data: portfolioList, isFetching: isPortfolioLoading },
    { data: pieChartData },
  ] = useQueries({
    queries: [
      {
        queryKey: [QueryKeys.PORTFOLIO_OVERVIEW],
        queryFn: () => axiosInstance.get(`/portfolios/dropdown/`),
      },
      {
        queryKey: [
          selectedItems,
          selectedAssetTypes,
          QueryKeys.INSIGHTS_HOLDINGS,
        ],
        queryFn: () =>
          axiosInstance.post(`/holdings/selected/`, {
            portfolios: selectedItems?.length ? selectedItems : [],
            instType: selectedAssetTypes,
          }),
        keepPreviousData: true,
      },
    ],
  });

  return (
    <DashboardLayout title="Holdings Insights">
      {!isFilterApplied ? (
        <Grid2 size={12} mt={10}>
          <NoData
            customImg={
              <img
                loading="lazy"
                alt=""
                src="/LandingPage/portfolio.svg"
                draggable="false"
              />
            }
            additionalComponent={
              <GKButton
                variant="contained"
                startIcon={<FilterIcon />}
                endIcon={<ArrowRightIcon />}
                onClick={() => setIsOpenModal(true)}
              >
                Apply Filter
              </GKButton>
            }
            text="No Portfolios Yet"
            subText="Subscribed baskets will appear here or you can also create a portfolio on behalf of your client."
          />
        </Grid2>
      ) : (
        <Grid2 container spacing={2}>
          <Grid2
            container
            size={12}
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderBottom: "1px solid var(--neutral-100)" }}
          >
            <Grid2 size="auto">
              <Tabs
                value={leadDetailsTabs}
                onChange={(e, val) => {
                  dispatch(TAB({ tabKey: "leadDetailsTabs", tabValue: val }));
                }}
              >
                <Tab disableRipple label="Summary" />
                <Tab disableRipple label="Allocation" />
                <Tab disableRipple label="Performance" />
                <Tab disableRipple label="Holdings" />
              </Tabs>
            </Grid2>

            <Grid2
              size="auto"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
            >
              {/* {selectedItems.length > 0 && (
                <>
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--grey-900)"
                  >
                    Portfolio Name:
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    {selectedItems[0]?.name}
                  </Typography>
                  {selectedItems.length > 1 && (
                    <Tooltip
                      title={
                        <div style={{ whiteSpace: "pre-line", width: "auto" }}>
                          {selectedItems
                            .slice(1)
                            .map((item) => item.name)
                            .join("\n")}
                        </div>
                      }
                      arrow
                    >
                      <Typography
                        fontSize={13}
                        fontWeight={500}
                        color="#006288"
                        sx={{
                          background: "#E6F2F7",
                          px: 1,
                          borderRadius: "50%",
                        }}
                      >
                        +{selectedItems.length - 1}
                      </Typography>
                    </Tooltip>
                  )}
                  <BigDotIcon
                    color="var(--neutral-300)"
                    width={20}
                    height={20}
                  />
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-700)"
                  >
                    {selectedAssetTypes.length === 0
                      ? "All"
                      : selectedAssetTypes[0] === "MF"
                        ? "Mutual Funds"
                        : "Equity"}
                  </Typography>
                </>
              )} */}
              <GKButton
                startIcon={<FilterIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={() => setIsOpenModal(true)}
                sx={{ border: "1px solid var(--neutral-100)" }}
              >
                Filter
              </GKButton>
            </Grid2>
          </Grid2>

          <Grid2 size={12}>
            {leadDetailsTabs === 0 && (
              <PortfolioBreakdown
                allHoldingsInsightsData={pieChartData?.data || []}
                portfolioList={portfolioList?.data || []}
                selectedItems={selectedItems}
                selectedAssetTypes={selectedAssetTypes}
                onApply={(portfolios, assetTypes) => {
                  setSelectedItems(portfolios);
                  setSelectedAssetTypes(assetTypes);
                  setIsFilterApplied(true);
                  setResetOverlapKey((prev) => prev + 1);
                }}
              />
            )}
            {leadDetailsTabs === 1 && (
              <InsigntsPageContent
                allHoldingsInsightsData={pieChartData?.data || []}
                isLoading={isPortfolioLoading}
                resetOverlapKey={resetOverlapKey}
                onRowClick={(row) => {
                  navigate(
                    `/app/insights-holdings/${row?.row?.gcode}/details`,
                    {
                      state: {
                        portfoliosIds: selectedItems
                          ?.map((data: any) => data?.portfolios)
                          ?.flat()
                          ?.map((dataItem) => dataItem?.value),
                      },
                    },
                  );
                }}
                selectedItems={selectedItems}
                setIsModalOpen={setIsOpenModal}
              />
            )}
            {leadDetailsTabs === 2 && (
              <Performance
                allHoldingsInsightsData={pieChartData?.data || []}
                portfolios={selectedItems}
                portfolioList={portfolioList?.data || []}
              />
            )}

            {leadDetailsTabs === 3 && (
              <HoldingsInsightsNew
                holdingData={holdingData}
                holdingLoading={holdingLoading}
                onRowClick={(row) => {
                  navigate(
                    `/app/insights-holdings/${row?.row?.gcode}/details`,
                    {
                      state: {
                        portfoliosIds: selectedItems
                          ?.map((data: any) => data?.portfolios)
                          ?.flat()
                          ?.map((dataItem) => dataItem?.value),
                      },
                    },
                  );
                }}
              />
            )}
          </Grid2>
        </Grid2>
      )}
      <FilterModal
        open={openModal}
        setOpen={setIsOpenModal}
        portfolioList={portfolioList?.data || []}
        isPortfolioLoading={isPortfolioLoading}
        selectedItems={selectedItems}
        selectedAssetTypes={selectedAssetTypes}
        onApply={(portfolios, assetTypes) => {
          setSelectedItems(portfolios);
          setSelectedAssetTypes(assetTypes);
          setIsFilterApplied(true);
          setResetOverlapKey((prev) => prev + 1);
        }}
      />
    </DashboardLayout>
  );
};

export default HoldingsInsights;
